
/**
 * Performs deep strict equality comparison between two values
 * @param a - First value to compare
 * @param b - Second value to compare
 * @returns true if values are deeply equal, false otherwise
 */
export function isDeepStrictEqual(a: unknown, b: unknown): boolean {
    // Handle primitive types and same reference
    if (a === b) {
        return true;
    }

    // Handle NaN case (NaN !== NaN in JavaScript)
    if (Number.isNaN(a) && Number.isNaN(b)) {
        return true;
    }

    // If either is null or not an object, they're not equal (already checked === above)
    if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') {
        return false;
    }

    // Handle Date objects
    if (a instanceof Date && b instanceof Date) {
        return a.getTime() === b.getTime();
    }

    // Handle RegExp objects
    if (a instanceof RegExp && b instanceof RegExp) {
        return a.source === b.source && a.flags === b.flags;
    }

    // Handle Arrays
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) {
            return false;
        }
        for (let i = 0; i < a.length; i++) {
            if (!isDeepStrictEqual(a[i], b[i])) {
                return false;
            }
        }
        return true;
    }

    // One is array, other is not
    if (Array.isArray(a) || Array.isArray(b)) {
        return false;
    }

    // Handle Map objects
    if (a instanceof Map && b instanceof Map) {
        if (a.size !== b.size) {
            return false;
        }
        const entriesA = Array.from(a.entries());
        for (let i = 0; i < entriesA.length; i++) {
            const [key, value] = entriesA[i];
            if (!b.has(key) || !isDeepStrictEqual(value, b.get(key))) {
                return false;
            }
        }
        return true;
    }

    // Handle Set objects
    if (a instanceof Set && b instanceof Set) {
        if (a.size !== b.size) {
            return false;
        }
        const valuesA = Array.from(a.values());
        for (let i = 0; i < valuesA.length; i++) {
            if (!b.has(valuesA[i])) {
                return false;
            }
        }
        return true;
    }

    // Handle plain objects
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) {
        return false;
    }

    for (const key of keysA) {
        if (!Object.prototype.hasOwnProperty.call(b, key)) {
            return false;
        }
        if (!isDeepStrictEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])) {
            return false;
        }
    }

    return true;
}